import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/components/BoardOfEd/components/StudentEnrollment.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase";

import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";

import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { HoverPopover } from "../../../shared/ui/overlay/HoverPopover.jsx";
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { PROGRAM_CONFIG } from "../../../shared/constants/programBudget.js";

import {
  LineChartWithToggles,
  BudgetInflationChart,
} from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import { buildInflationChartData } from "../../../shared/ui/chart/charts/programBudgetCharts.js";

import {
  getDocIdFromProgramKey,
  sumSectionTotals,
} from "../../../budget/utils/programBudgetUtils.js";

import {
  buildBudgetTotalsByYear,
  buildDistrictCostPerStudentSeries,
  buildCostPerStudentRowsForInflation,
  COST_PER_STUDENT_FIELD_DEFS,
  toYearNumber,
  buildTwoLineHeader,
  attachIncreaseFields,
  computeAverageClassSize,
  formatAvgClassSize,
  formatCount,
  slugifyKey,
  gradeRank,
  gradeLabel,
  getEligibleYears,
  getDistrictEnrollmentForYearFromDoc,
  getSchoolEnrollmentForYearFromDoc,
  buildFiscalYearLabels,
  buildSchoolsMeta,
  sortSchoolKeysByLevelThenName,
  sectionForLevel,
  buildEnrollmentBreakdownPieItems,
} from "../../../features/boe/utils/studentEnrollmentUtils.js";

const STUDENT_COLLECTION = "publicSchools";
const STUDENT_DOC_ID = "studentEnrollment";

function SchoolLabelWithPopover({ label, latestGrades }) {
  const filteredGrades =
    latestGrades && typeof latestGrades === "object"
      ? Object.entries(latestGrades).filter(
          ([, v]) => typeof v === "number" && Number.isFinite(v) && v !== 0,
        )
      : [];

  const hasGrades = filteredGrades.length > 0;
  if (!hasGrades) return <span>{label}</span>;

  const content = (
    <div className="p-3 text-xs">
      <div className="font-semibold text-sm text-gray-900 mb-2">{label}</div>

      <div className="font-semibold text-gray-800 mb-1">
        Grades (latest year)
      </div>
      <ul className="space-y-0.5">
        {filteredGrades
          .sort(([a], [b]) => {
            const ra = gradeRank(a);
            const rb = gradeRank(b);
            if (ra !== rb) return ra - rb;
            return String(a).localeCompare(String(b));
          })
          .map(([g, v]) => (
            <li key={g} className="flex justify-between gap-4">
              <span className="text-gray-700">{gradeLabel(g)}</span>
              <span className="text-gray-900 font-medium">
                {formatCount(v)}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );

  return (
    <div className="relative flex items-center min-h-[22px] pr-6">
      <span className="leading-tight">{label}</span>
      <div className="absolute right-1 top-1/2 -translate-y-1/2">
        <HoverPopover
          content={content}
          popoverClassName="text-gray-800"
          maxWidth={520}
        >
          <InformationCircleIcon
            className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
            aria-label={`Show details for ${label}`}
          />
        </HoverPopover>
      </div>
    </div>
  );
}

export function StudentEnrollment() {
  const excelExportRef = useRef(null);

  const [docData, setDocData] = useState(null);
  const [yearNumbers, setYearNumbers] = useState([]);
  const [endingYear, setEndingYear] = useSharedFilter("StudentEnrollment.jsx:StudentEnrollment:endingYear", null);
  const [loading, setLoading] = useState(true);

  // ✅ Snapshot tables toggle (matches StaffSnapshot UX)
  const [showSnapshotTables, setShowSnapshotTables] = useState(true);

  // Chart toggles (stay independent + always visible)
  const [showDistrictChart, setShowDistrictChart] = useState(false);
  const [showSchoolsChart, setShowSchoolsChart] = useState(false);
  const [showClassSizeChart, setShowClassSizeChart] = useState(false);
  const [showEnrollmentPieChart, setShowEnrollmentPieChart] = useState(false);
  const [
    showCostPerStudentInflationChart,
    setShowCostPerStudentInflationChart,
  ] = useState(false);
  const [
    showApprovedPricePerStudentChart,
    setShowApprovedPricePerStudentChart,
  ] = useState(false);
  const [showActualPricePerStudentChart, setShowActualPricePerStudentChart] =
    useState(false);

  const [budgetProgramDocs, setBudgetProgramDocs] = useState({});
  const [inflationByYear, setInflationByYear] = useState({});
  const [pieYear, setPieYear] = useSharedFilter("StudentEnrollment.jsx:StudentEnrollment:pieYear", null);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);

      try {
        const programDocIds = PROGRAM_CONFIG.map((p) =>
          getDocIdFromProgramKey(p.key),
        );

        const [enrollmentSnap, inflSnap, ...programSnaps] = await Promise.all([
          getDoc(doc(db, STUDENT_COLLECTION, STUDENT_DOC_ID)),
          getDoc(doc(db, "common", "inflationByYear")),
          ...programDocIds.map((id) => getDoc(doc(db, "publicSchools", id))),
        ]);

        if (!alive) return;

        // ----- Enrollment doc -----
        if (!enrollmentSnap.exists()) {
          console.warn(
            `Student enrollment doc missing: ${STUDENT_COLLECTION}/${STUDENT_DOC_ID}`,
          );
          setDocData(null);
          setYearNumbers([]);
          setEndingYear.initialize(null);
        } else {
          const data = enrollmentSnap.data();
          setDocData(data);

          const yearsObj = data?.years || {};
          const ys = Object.keys(yearsObj)
            .map(toYearNumber)
            .filter(Boolean)
            .sort((a, b) => a - b);

          setYearNumbers(ys);
        }

        // ----- Inflation doc -----
        if (!inflSnap.exists()) {
          console.warn("Inflation doc missing: common/inflationByYear");
          setInflationByYear({});
        } else {
          const inflData = inflSnap.data();
          setInflationByYear(inflData?.years || {});
        }

        // ----- Budget program docs -----
        const docsById = {};
        programSnaps.forEach((snap, idx) => {
          const id = programDocIds[idx];
          if (!snap.exists()) {
            console.warn(`BoE program doc missing: publicSchools/${id}`);
            return;
          }
          docsById[id] = snap.data();
        });

        setBudgetProgramDocs(docsById);
      } catch (e) {
        console.error("Error loading student enrollment / budget docs:", e);
        if (alive) {
          setDocData(null);
          setYearNumbers([]);
          setEndingYear.initialize(null);
          setBudgetProgramDocs({});
          setInflationByYear({});
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const schoolsMeta = useMemo(() => buildSchoolsMeta(docData), [docData]);

  const enrollmentBreakdownPieItems = useMemo(() => {
    return buildEnrollmentBreakdownPieItems({ docData, schoolsMeta, pieYear });
  }, [docData, schoolsMeta, pieYear]);

  const yearSet = useMemo(() => new Set(yearNumbers), [yearNumbers]);

  const endingYearOptions = useMemo(() => {
    return yearNumbers.filter(
      (y) =>
        yearSet.has(y) &&
        yearSet.has(y - 1) &&
        yearSet.has(y - 2) &&
        yearSet.has(y - 3),
    );
  }, [yearNumbers, yearSet]);

  useEffect(() => {
    if (!endingYearOptions.length) return;
    setEndingYear.initialize((prev) =>
      prev && endingYearOptions.includes(prev)
        ? prev
        : endingYearOptions[endingYearOptions.length - 1],
    );
  }, [endingYearOptions]);

  const windowYearsMeta = useMemo(() => {
    if (!endingYear) return [];
    return [endingYear - 3, endingYear - 2, endingYear - 1, endingYear].map(
      (y) => ({
        year: y,
        colKey: `y_${y}`,
      }),
    );
  }, [endingYear]);

  const pieYearOptions = useMemo(() => {
    return [...yearNumbers].sort((a, b) => a - b);
  }, [yearNumbers]);

  useEffect(() => {
    if (!pieYearOptions.length) return;

    setPieYear.initialize((prev) => {
      const preferred =
        endingYear && pieYearOptions.includes(endingYear)
          ? endingYear
          : pieYearOptions[pieYearOptions.length - 1];

      return prev && pieYearOptions.includes(prev) ? prev : preferred;
    });
  }, [pieYearOptions, endingYear]);

  const sortedSchoolKeys = useMemo(
    () => sortSchoolKeysByLevelThenName(schoolsMeta),
    [schoolsMeta],
  );

  const sectionOrder = useMemo(
    () => [
      "Elementary Schools",
      "Middle School",
      "High School",
      "District Totals",
    ],
    [],
  );

  const tableColumns = useMemo(() => {
    if (!windowYearsMeta.length) return [];

    const LABEL_COL_WIDTH = "320px";
    const YEAR_COL_WIDTH = "140px";
    const DELTA_COL_WIDTH = "110px";

    const cols = [
      {
        key: "label",
        label: "",
        style: { width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH },
        editable: false,
        render: (row) => (
          <SchoolLabelWithPopover
            label={row.label}
            latestGrades={row._latestGrades}
          />
        ),
      },
    ];

    const lastColKey = windowYearsMeta[windowYearsMeta.length - 1]?.colKey;

    windowYearsMeta.forEach(({ year, colKey }) => {
      const fy = formatFiscalYearFromStart(year);
      const isHighlighted = colKey === lastColKey;

      cols.push({
        key: colKey,
        label: buildTwoLineHeader(fy),
        align: "right",
        style: { width: YEAR_COL_WIDTH, minWidth: YEAR_COL_WIDTH },
        headerClassName: isHighlighted ? "bg-yellow-100" : undefined,
        cellClassName: isHighlighted ? "bg-yellow-50" : undefined,
      });
    });

    cols.push(
      {
        key: "increase",
        label: "Change",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
      },
      {
        key: "percentIncrease",
        label: "% Change",
        format: "percent",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
      },
    );

    return cols;
  }, [windowYearsMeta]);

  const classSizeTableColumns = useMemo(() => {
    if (!windowYearsMeta.length) return [];

    const LABEL_COL_WIDTH = "320px";
    const YEAR_COL_WIDTH = "140px";
    const DELTA_COL_WIDTH = "110px";

    const cols = [
      {
        key: "label",
        label: "",
        style: { width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH },
        editable: false,
      },
    ];

    const lastColKey = windowYearsMeta[windowYearsMeta.length - 1]?.colKey;

    windowYearsMeta.forEach(({ year, colKey }) => {
      const fy = formatFiscalYearFromStart(year);
      const isHighlighted = colKey === lastColKey;

      cols.push({
        key: colKey,
        label: buildTwoLineHeader(fy, "Avg Class Size"),
        align: "right",
        style: { width: YEAR_COL_WIDTH, minWidth: YEAR_COL_WIDTH },
        headerClassName: isHighlighted ? "bg-yellow-100" : undefined,
        cellClassName: isHighlighted ? "bg-yellow-50" : undefined,
        render: (row) => formatAvgClassSize(row[colKey]),
      });
    });

    cols.push(
      {
        key: "increase",
        label: "Change",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
        render: (row) => formatAvgClassSize(row.increase),
      },
      {
        key: "percentIncrease",
        label: "% Change",
        format: "percent",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
      },
    );

    return cols;
  }, [windowYearsMeta]);

  const classSizeTableRows = useMemo(() => {
    if (!docData?.years || !windowYearsMeta.length) return [];

    const yearsObj = docData.years;
    const elementaryKeys = sortedSchoolKeys.filter(
      (k) => schoolsMeta?.[k]?.level === "elementary",
    );

    const rows = [];

    rows.push({ section: "Elementary Schools", label: "Elementary Schools" });

    for (const schoolKey of elementaryKeys) {
      const meta = schoolsMeta[schoolKey];
      const r = { label: meta?.name || schoolKey };

      windowYearsMeta.forEach(({ year, colKey }) => {
        const school = yearsObj?.[String(year)]?.schools?.[schoolKey];
        const avg = computeAverageClassSize(school?.classes);
        r[colKey] =
          typeof avg === "number" && Number.isFinite(avg) ? avg : null;
      });

      const hasAny = windowYearsMeta.some(
        ({ colKey }) =>
          typeof r[colKey] === "number" && Number.isFinite(r[colKey]),
      );
      if (!hasAny) continue;

      attachIncreaseFields(r, windowYearsMeta);
      rows.push(r);
    }

    rows.push({ section: "District Totals", label: "District Totals" });

    const districtRow = { label: "Elementary Avg (All Schools)", bold: true };

    windowYearsMeta.forEach(({ year, colKey }) => {
      const schools = yearsObj?.[String(year)]?.schools || {};

      let sum = 0;
      let count = 0;

      for (const schoolKey of elementaryKeys) {
        const cls = schools?.[schoolKey]?.classes;
        if (!cls || typeof cls !== "object") continue;

        for (const arr of Object.values(cls)) {
          if (!Array.isArray(arr)) continue;
          for (const v of arr) {
            if (typeof v === "number" && Number.isFinite(v) && v > 0) {
              sum += v;
              count += 1;
            }
          }
        }
      }

      districtRow[colKey] = count > 0 ? sum / count : null;
    });

    attachIncreaseFields(districtRow, windowYearsMeta);
    rows.push(districtRow);

    return rows;
  }, [docData, windowYearsMeta, sortedSchoolKeys, schoolsMeta]);

  const tableRows = useMemo(() => {
    if (!docData?.years || !windowYearsMeta.length) return [];

    const yearsObj = docData.years;
    const rows = [];

    const emittedSection = new Set();

    for (const schoolKey of sortedSchoolKeys) {
      const meta = schoolsMeta[schoolKey];
      const section = sectionForLevel(meta?.level);

      if (!emittedSection.has(section)) {
        rows.push({ section, label: section });
        emittedSection.add(section);
      }

      const row = { label: meta?.name || schoolKey };

      const latestYear = windowYearsMeta[windowYearsMeta.length - 1]?.year;
      const latestSchool = yearsObj?.[String(latestYear)]?.schools?.[schoolKey];
      row._latestGrades = latestSchool?.grades || null;

      windowYearsMeta.forEach(({ year, colKey }) => {
        const school = yearsObj?.[String(year)]?.schools?.[schoolKey];
        const v = school?.total;
        row[colKey] = typeof v === "number" && Number.isFinite(v) ? v : null;
      });

      attachIncreaseFields(row, windowYearsMeta);
      rows.push(row);
    }

    rows.push({ section: "District Totals", label: "District Totals" });

    const districtKeysInOrder = [
      ["total", "In-District Total"],
      ["preK", "Pre-K"],
      ["outOfDistrict", "Out of District"],
      ["ghsAlternateProgram", "GHS Alternate Program"],
      ["grandTotalComputed", "Grand Total"],
    ];

    districtKeysInOrder.forEach(([k, label], idx) => {
      const isBold = k === "grandTotalComputed" || k === "total";
      const r = { label, bold: isBold, order: idx };

      windowYearsMeta.forEach(({ year, colKey }) => {
        if (k === "grandTotalComputed") {
          const dt = yearsObj?.[String(year)]?.districtTotals;
          const base = dt?.total;
          const preK = dt?.preK;
          const ood = dt?.outOfDistrict;

          const ok =
            typeof base === "number" &&
            typeof preK === "number" &&
            typeof ood === "number" &&
            Number.isFinite(base) &&
            Number.isFinite(preK) &&
            Number.isFinite(ood);

          r[colKey] = ok ? base + preK + ood : null;
          return;
        }

        const v = yearsObj?.[String(year)]?.districtTotals?.[k];
        r[colKey] = typeof v === "number" && Number.isFinite(v) ? v : null;
      });

      attachIncreaseFields(r, windowYearsMeta);
      rows.push(r);
    });

    return rows;
  }, [docData, windowYearsMeta, sortedSchoolKeys, schoolsMeta]);

  const allYears = useMemo(
    () => yearNumbers.map((y) => String(y)),
    [yearNumbers],
  );

  const classSizeChart = useMemo(() => {
    if (!docData?.years || !allYears.length) return { labels: [], series: [] };

    const labels = buildFiscalYearLabels(allYears);

    const elementaryKeys = sortedSchoolKeys.filter((k) => {
      if (schoolsMeta?.[k]?.level !== "elementary") return false;
      return allYears.some((y) => {
        const cls = docData?.years?.[y]?.schools?.[k]?.classes;
        const avg = computeAverageClassSize(cls);
        return typeof avg === "number" && Number.isFinite(avg);
      });
    });

    const elementaryAvgSeries = {
      key: "ElementaryAvg",
      label: "Elementary Avg (All Schools)",
      data: allYears.map((y) => {
        const schools = docData?.years?.[y]?.schools || {};
        let sum = 0;
        let count = 0;

        for (const schoolKey of elementaryKeys) {
          const cls = schools?.[schoolKey]?.classes;
          if (!cls || typeof cls !== "object") continue;

          for (const arr of Object.values(cls)) {
            if (!Array.isArray(arr)) continue;
            for (const v of arr) {
              if (typeof v === "number" && Number.isFinite(v) && v > 0) {
                sum += v;
                count += 1;
              }
            }
          }
        }

        return count > 0 ? sum / count : null;
      }),
      group: "elementary",
      valueType: "count",
      defaultVisible: true,
    };

    const schoolSeries = elementaryKeys.map((schoolKey) => {
      const meta = schoolsMeta[schoolKey];
      return {
        key: `school_${schoolKey}`,
        label: meta?.name || schoolKey,
        data: allYears.map((y) => {
          const school = docData?.years?.[y]?.schools?.[schoolKey];
          return computeAverageClassSize(school?.classes);
        }),
        group: "elementary",
        valueType: "count",
        defaultVisible: false,
      };
    });

    return { labels, series: [elementaryAvgSeries, ...schoolSeries] };
  }, [docData, allYears, sortedSchoolKeys, schoolsMeta]);

  const districtChart = useMemo(() => {
    if (!docData?.years || !allYears.length) return { labels: [], series: [] };

    const labels = buildFiscalYearLabels(allYears);

    const series = [
      {
        key: "Total",
        label: "District – Total",
        data: allYears.map(
          (y) => docData?.years?.[y]?.districtTotals?.grandTotal ?? null,
        ),
        group: "district",
        valueType: "count",
        defaultVisible: false,
      },
      {
        key: "inDistrict",
        label: "District – In-District Total",
        data: allYears.map(
          (y) => docData?.years?.[y]?.districtTotals?.total ?? null,
        ),
        group: "district",
        valueType: "count",
        defaultVisible: false,
      },
      {
        key: "outOfDistrict",
        label: "District – Out of District",
        data: allYears.map(
          (y) => docData?.years?.[y]?.districtTotals?.outOfDistrict ?? null,
        ),
        group: "district",
        valueType: "count",
        defaultVisible: false,
      },
      {
        key: "preK",
        label: "District – Pre-K",
        data: allYears.map(
          (y) => docData?.years?.[y]?.districtTotals?.preK ?? null,
        ),
        group: "district",
        valueType: "count",
        defaultVisible: false,
      },
      {
        key: "altProgram",
        label: "District – GHS Alternate Program",
        data: allYears.map(
          (y) =>
            docData?.years?.[y]?.districtTotals?.ghsAlternateProgram ?? null,
        ),
        group: "district",
        valueType: "count",
        defaultVisible: false,
      },
    ];

    return { labels, series };
  }, [docData, allYears]);

  const schoolsChart = useMemo(() => {
    if (!docData?.years || !allYears.length) return { labels: [], series: [] };

    const labels = buildFiscalYearLabels(allYears);

    const totalSeries = {
      key: "Total",
      label: "District – Total",
      data: allYears.map(
        (y) => docData?.years?.[y]?.districtTotals?.grandTotal ?? null,
      ),
      group: "district",
      valueType: "count",
      defaultVisible: false,
    };

    const schoolSeries = sortedSchoolKeys.map((schoolKey) => {
      const meta = schoolsMeta[schoolKey];
      const group =
        meta?.level === "elementary"
          ? "elementary"
          : meta?.level === "middle"
            ? "middle"
            : meta?.level === "secondary"
              ? "high"
              : "schools";

      return {
        key: slugifyKey(schoolKey),
        label: meta?.name || schoolKey,
        data: allYears.map(
          (y) => docData?.years?.[y]?.schools?.[schoolKey]?.total ?? null,
        ),
        group,
        valueType: "count",
        defaultVisible: false,
      };
    });

    return { labels, series: [totalSeries, ...schoolSeries] };
  }, [docData, allYears, sortedSchoolKeys, schoolsMeta]);

  const budgetTotalsByYear = useMemo(
    () => buildBudgetTotalsByYear(budgetProgramDocs),
    [budgetProgramDocs],
  );

  const approvedPricePerStudentChart = useMemo(() => {
    return buildDistrictCostPerStudentSeries({
      docData,
      yearNumbers,
      budgetTotalsByYear,
      kind: "approved",
    });
  }, [docData, yearNumbers, budgetTotalsByYear]);

  const actualPricePerStudentChart = useMemo(() => {
    return buildDistrictCostPerStudentSeries({
      docData,
      yearNumbers,
      budgetTotalsByYear,
      kind: "actual",
    });
  }, [docData, yearNumbers, budgetTotalsByYear]);

  const approvedCostPerStudentRowsForInflation = useMemo(() => {
    return buildCostPerStudentRowsForInflation({
      docData,
      yearNumbers,
      budgetTotalsByYear,
      kind: "approved",
    });
  }, [docData, yearNumbers, budgetTotalsByYear]);

  const proposedCostPerStudentRowsForInflation = useMemo(() => {
    return buildCostPerStudentRowsForInflation({
      docData,
      yearNumbers,
      budgetTotalsByYear,
      kind: "proposedOnly",
    });
  }, [docData, yearNumbers, budgetTotalsByYear]);

  const actualCostPerStudentRowsForInflation = useMemo(() => {
    return buildCostPerStudentRowsForInflation({
      docData,
      yearNumbers,
      budgetTotalsByYear,
      kind: "actual",
    });
  }, [docData, yearNumbers, budgetTotalsByYear]);

  const {
    costInflationLabels,
    approvedCostInflationSeries,
    actualCostInflationSeries,
    costInflationBaselineSeries,
    costInflationBaselineSeriesForActual,
  } = useMemo(() => {
    const {
      inflationLabels,
      approvedInflationSeries,
      actualInflationSeries,
      inflationBaselineSeries,
      inflationBaselineSeriesForActual,
    } = buildInflationChartData(
      approvedCostPerStudentRowsForInflation,
      actualCostPerStudentRowsForInflation,
      proposedCostPerStudentRowsForInflation,
      inflationByYear,
      COST_PER_STUDENT_FIELD_DEFS,
      ["costPerStudent"],
    );

    return {
      costInflationLabels: inflationLabels,
      approvedCostInflationSeries: approvedInflationSeries,
      actualCostInflationSeries: actualInflationSeries,
      costInflationBaselineSeries: inflationBaselineSeries,
      costInflationBaselineSeriesForActual: inflationBaselineSeriesForActual,
    };
  }, [
    approvedCostPerStudentRowsForInflation,
    actualCostPerStudentRowsForInflation,
    proposedCostPerStudentRowsForInflation,
    inflationByYear,
    COST_PER_STUDENT_FIELD_DEFS,
  ]);

  // ---- Render ----
  if (loading) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Loading student enrollment…
      </div>
    );
  }

  if (
    !docData ||
    !endingYear ||
    !windowYearsMeta.length ||
    tableRows.length === 0
  ) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Student enrollment data is not available (or missing a 4-year window).
      </div>
    );
  }

  return (
    <TableSearchScope>
    <div className="space-y-8">
      {/* ✅ Snapshot Tables (collapsible) */}
      <div className="space-y-3">
        <HeaderToggleButton
          isOpen={showSnapshotTables}
          onToggle={() => setShowSnapshotTables((prev) => !prev)}
          title="Student Enrollment"
          ariaLabel="Toggle student enrollment snapshot tables"
          controlsId="student-enrollment-snapshots"
          rightSlot={
            endingYearOptions.length > 0 ? (
              <div
                className="ml-auto flex flex-wrap items-center justify-end gap-2 pr-2 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Excel export button (unstyled icon) */}
                <button
                  type="button"
                  className="inline-flex items-center justify-center bg-transparent border-0 p-0 m-0 text-gray-700 hover:text-gray-900 focus:outline-none leading-none"
                  style={{ lineHeight: 1 }}
                  title="Download table to Excel"
                  aria-label="Download table to Excel"
                  onClick={(e) => {
                    e.stopPropagation();
                    excelExportRef.current?.exportToExcel?.();
                  }}
                >
                  <span className="relative top-[1px] text-base">💾</span>
                </button>
                {showSnapshotTables && <SectionTableSearch />}

                <span className="whitespace-nowrap font-semibold text-xs text-gray-700">
                  Ending Year
                </span>

                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={endingYear || ""}
                  onChange={(e) =>
                    setEndingYear(
                      e.target.value ? Number(e.target.value) : endingYear,
                    )
                  }
                >
                  {endingYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {formatFiscalYearFromStart(y)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null
          }
        />

        {showSnapshotTables && (
          <div id="student-enrollment-snapshots" className="space-y-8">
            {/* Enrollment Snapshot Table */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">
                  Student Enrollment Snapshot by Year
                </h3>
              </div>

              <CommonTable
                excelExportRef={excelExportRef}
                excelFilename={`student-enrollment-snapshot-${endingYear || "latest"}.xlsx`}
                excelSheetName="Enrollment Snapshot"
                excelTitle="Student Enrollment Snapshot by Year"
                title={null}
                columns={tableColumns}
                data={tableRows}
                isEditing={false}
                headerColor="bg-[#e5eef9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
                sectionOrder={sectionOrder}
              />
            </div>

            {/* Avg Class Size Snapshot Table */}
            <div className="mt-6 space-y-2">
              <h3 className="text-lg font-semibold">
                Average Class Size Snapshot by Year
              </h3>

              <CommonTable
                excelExportRef={excelExportRef}
                excelFilename={`student-enrollment-class-size-${endingYear || "latest"}.xlsx`}
                excelSheetName="Avg Class Size"
                excelTitle="Average Class Size Snapshot by Year"
                title={null}
                columns={classSizeTableColumns}
                data={classSizeTableRows}
                isEditing={false}
                headerColor="bg-[#e5eef9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
                sectionOrder={["Elementary Schools", "District Totals"]}
              />
            </div>
          </div>
        )}
      </div>

      {/* ✅ Charts remain available even when snapshot tables are collapsed */}

      {/* District Totals Chart */}
      {districtChart.labels.length > 0 && districtChart.series.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showDistrictChart}
            onToggle={() => setShowDistrictChart((p) => !p)}
            title="Students – District Totals (Over Time)"
            ariaLabel="Toggle district enrollment chart"
            controlsId="students-district-totals-chart"
          />

          {showDistrictChart && (
            <div id="students-district-totals-chart">
              <LineChartWithToggles
                labels={districtChart.labels}
                series={districtChart.series}
                showTotalWhenNoneSelected={true}
                showSeriesToggles={true}
                totalLabel="District – Total"
              />
            </div>
          )}
        </div>
      )}

      {/* Schools Chart */}
      {schoolsChart.labels.length > 0 && schoolsChart.series.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showSchoolsChart}
            onToggle={() => setShowSchoolsChart((p) => !p)}
            title="Students – Enrollment by School (Over Time)"
            ariaLabel="Toggle school enrollment chart"
            controlsId="students-enrollment-by-school-chart"
          />

          {showSchoolsChart && (
            <div id="students-enrollment-by-school-chart">
              <LineChartWithToggles
                labels={schoolsChart.labels}
                series={schoolsChart.series}
                showTotalWhenNoneSelected={true}
                showSeriesToggles={true}
                totalLabel="District – Total"
              />
            </div>
          )}
        </div>
      )}

      {/* Class Size Chart */}
      {classSizeChart.labels.length > 0 && classSizeChart.series.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showClassSizeChart}
            onToggle={() => setShowClassSizeChart((p) => !p)}
            title="Students – Average Class Size (Elementary, Over Time)"
            ariaLabel="Toggle average class size chart"
            controlsId="students-avg-class-size-chart"
          />

          {showClassSizeChart && (
            <div id="students-avg-class-size-chart">
              <LineChartWithToggles
                labels={classSizeChart.labels}
                series={classSizeChart.series}
                showTotalWhenNoneSelected={false}
                showSeriesToggles={true}
                defaultVisibleKeys={["ElementaryAvg"]}
                totalLabel="Elementary Avg (All Schools)"
              />
            </div>
          )}
        </div>
      )}

      {/* Approved Raw PPE */}
      {approvedPricePerStudentChart.labels.length > 0 &&
        approvedPricePerStudentChart.series.length > 0 && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showApprovedPricePerStudentChart}
              onToggle={() => setShowApprovedPricePerStudentChart((p) => !p)}
              title="Students – Approved Raw Cost per Student"
              ariaLabel="Toggle approved cost per student chart"
              controlsId="students-budgeted-price-per-student-chart"
            />

            {showApprovedPricePerStudentChart && (
              <div id="students-budgeted-price-per-student-chart">
                <p className="text-xs text-gray-600 mb-1">
                  Shows the district’s total{" "}
                  <span className="font-semibold">budgeted</span> Board of
                  Education program cost divided by enrollment. This is also
                  called "Raw PPE", and this value is not used by Connecticut
                  schools for purposes of cost per student.
                </p>

                <LineChartWithToggles
                  labels={approvedPricePerStudentChart.labels}
                  series={approvedPricePerStudentChart.series}
                  groups={approvedPricePerStudentChart.groups}
                  showTotalWhenNoneSelected={false}
                  showSeriesToggles={true}
                  proposedRenderMode="merge"
                  showChartTypeSelector={true}
                  defaultYAxisMode="zero"
                />
              </div>
            )}
          </div>
        )}

      {/* Actual Raw PPE */}
      {actualPricePerStudentChart.labels.length > 0 &&
        actualPricePerStudentChart.series.length > 0 && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showActualPricePerStudentChart}
              onToggle={() => setShowActualPricePerStudentChart((p) => !p)}
              title="Students – Actual Raw Cost per Student"
              ariaLabel="Toggle actual cost per student chart"
              controlsId="students-actual-price-per-student-chart"
            />

            {showActualPricePerStudentChart && (
              <div id="students-actual-price-per-student-chart">
                <p className="text-xs text-gray-600 mb-1">
                  Shows the district’s total{" "}
                  <span className="font-semibold">actual</span> Board of
                  Education program spending divided by enrollment
                  (district-wide or by school). This is also called "Raw PPE",
                  and this value is not used by Connecticut schools for purposes
                  of cost per student.
                </p>

                <LineChartWithToggles
                  labels={actualPricePerStudentChart.labels}
                  series={actualPricePerStudentChart.series}
                  groups={actualPricePerStudentChart.groups}
                  showTotalWhenNoneSelected={false}
                  showSeriesToggles={true}
                  showChartTypeSelector={true}
                  defaultYAxisMode="zero"
                />
              </div>
            )}
          </div>
        )}

      {/* Cost per Student vs Inflation */}
      {costInflationLabels.length > 0 &&
        (approvedCostInflationSeries.length > 0 ||
          actualCostInflationSeries.length > 0) && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showCostPerStudentInflationChart}
              onToggle={() => setShowCostPerStudentInflationChart((p) => !p)}
              title="Students – Raw Cost per Student vs Inflation"
              ariaLabel="Toggle cost per student vs inflation chart"
              controlsId="students-cost-per-student-inflation-chart"
            />

            {showCostPerStudentInflationChart && (
              <div id="students-cost-per-student-inflation-chart">
                <p className="text-xs text-gray-600 mb-1">
                  Compares district-wide cost per student to an inflation
                  baseline. This is also called "Raw PPE", and this value is not
                  used by Connecticut schools for purposes of cost per student.
                </p>

                <BudgetInflationChart
                  labels={costInflationLabels}
                  approvedData={approvedCostInflationSeries}
                  actualData={actualCostInflationSeries}
                  inflationData={costInflationBaselineSeries}
                  inflationDataForActual={costInflationBaselineSeriesForActual}
                />
              </div>
            )}
          </div>
        )}

      {/* Enrollment Breakdown Pie Chart */}
      {pieYearOptions.length > 0 && enrollmentBreakdownPieItems.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showEnrollmentPieChart}
            onToggle={() => setShowEnrollmentPieChart((p) => !p)}
            title="Students – Breakdown (Selected Year)"
            ariaLabel="Toggle student breakdown pie chart"
            controlsId="students-enrollment-breakdown-pie"
          />

          {showEnrollmentPieChart && (
            <div id="students-enrollment-breakdown-pie">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <p className="text-xs text-gray-600">
                  Pie chart showing enrollment share by category for the
                  selected fiscal year.
                </p>

                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold">Year</span>
                  <select
                    className="border rounded px-2 py-1 text-xs"
                    value={pieYear || ""}
                    onChange={(e) =>
                      setPieYear(
                        e.target.value ? Number(e.target.value) : pieYear,
                      )
                    }
                  >
                    {pieYearOptions.map((y) => (
                      <option key={y} value={y}>
                        {formatFiscalYearFromStart(y)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <PieChartWithToggles
                title={null}
                items={enrollmentBreakdownPieItems}
                percentOf="all"
                showLegend={false}
                showSeriesToggles={false}
                showChartTypeSelector={false}
                aggregateOthers={false}
                includeZeroValues={true}
                showLegendTable={true}
                legendTablePlacement="right"
                legendTableMaxHeight="420px"
                enableHoverPop={true}
                hoverPopOffset={12}
                sliceOrder="valueDesc"
              />
            </div>
          )}
        </div>
      )}
    </div>
    </TableSearchScope>
  );
}
